import { tm } from '../tm'
import { Donate } from './CardFooter/Donate'
import { Select } from './CardFooter/Select'
import { Show } from './CardFooter/Show'
import { CardImage } from './CardImage'
import type { ButtonProps } from '../Button'
import type {
  Amount,
  Locales,
  ProjectId,
  Project as ProjectType,
} from '@captaincause/types'
import type { CSSProperties, ReactNode } from 'react'

const emptyObject = {}

type Variant = 'give' | 'widget' | 'donation' | 'console' | 'catalog'

interface BaseProjectCardProps {
  project: ProjectType
  variant: Variant
  disabled?: boolean
  selected?: boolean
  hasPriorityImage?: boolean
  locale: Locales
  className?: string
  style?: CSSProperties
  pointMode?: boolean
  pointsValueInCents?: number | null
  amount?: Amount
  buttonStyle?: CSSProperties
  progressBar?: boolean
}

interface DonateProps extends BaseProjectCardProps {
  variant: 'give' | 'widget' | 'donation'
  donate: (id: ProjectId) => void
  amount: Amount | undefined
  compact: boolean
}

interface SelectProps extends BaseProjectCardProps {
  variant: 'console'
  toggleSelection: (id: ProjectId) => void
  seeMore: (id: ProjectId) => void
  disabled: boolean
  selected: boolean
}

interface ShowProps extends BaseProjectCardProps {
  variant: 'catalog'
  promoted: boolean
  Link?: (props: ButtonProps) => ReactNode
  seeMore?: (id: ProjectId) => void
}

export type ProjectCardProps = DonateProps | SelectProps | ShowProps

export function ProjectCard(props: ProjectCardProps) {
  const {
    project,
    variant,
    disabled = false,
    hasPriorityImage = false,
    pointsValueInCents,
    amount,
    pointMode = false,
    locale,
    className = '',
    progressBar = false,
  } = props

  const showPointAmount = pointMode && amount && variant === 'console'
  const showContributorsCount = variant === 'catalog'
  const showGemBadge =
    variant !== 'donation' && variant !== 'give' && variant !== 'widget'
  const showBadge = variant !== 'donation'
  const showProjectName = variant === 'donation' && progressBar
  const withTranslate = variant !== 'donation'

  return (
    <article
      className={tm(
        'relative mx-auto flex w-full max-w-[400px] flex-col justify-between rounded-2xl @container focus-visible:shadow-md md:mx-[initial] md:max-w-none',
        withTranslate &&
          'transition-transform duration-300 ease-out hover:-translate-y-2 focus-visible:-translate-y-2',
        className,
      )}
    >
      <CardImage
        locale={locale}
        project={project}
        amount={amount}
        showPointAmount={showPointAmount}
        pointsValueInCents={pointsValueInCents}
        showContributorsCount={showContributorsCount}
        showGemBadge={showGemBadge}
        showBadges={showBadge}
        showProjectName={showProjectName}
        hasPriorityImage={hasPriorityImage}
        className={tm(
          variant === 'widget' && 'h-[150px] max-h-[150px] min-h-[unset]',
          disabled && 'opacity-50',
        )}
      />
      {(variant === 'give' ||
        variant === 'widget' ||
        variant === 'donation') && <Donate {...props} />}
      {variant === 'console' && <Select {...props} />}
      {variant === 'catalog' && <Show {...props} />}
    </article>
  )
}
